import { Component, OnInit, inject } from "@angular/core";
import { TaskWorkspaceModel } from "@app/tasks/models/TaskWorkspaceModel";
import { TaskDataService } from "@app/tasks/services/TaskDataService";
import { Pages } from "@constants/pages/Pages";
import { FormatType } from "@enums/FormatType";
import { ModuleType } from "@enums/ModuleType";
import { RendererType } from "@enums/RendererType";
import { TabEventListenerType } from "@enums/TabEventListenerType.enum";
import { nameof } from "@methods/CommonMethods";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { TabService } from "@services/tabs/TabService";
import { firstValueFrom } from "rxjs";
import { injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/tasks/i18n/scope";
import { WorkspaceComponent } from "@app/shared/workspace/workspace.component";

@Component({
    selector: "app-task-overview",
    templateUrl: "./task-overview.component.html",
    styleUrls: [],
    imports: [WorkspaceComponent],
    viewProviders: [provideTranslationScope(scope)]
})
export class TaskOverviewComponent implements OnInit {
    private _taskDs = inject(TaskDataService);
    private _tabs = inject(TabService);
    private _translation = injectTranslation(scope);
    pages = inject(Pages);
    private _permissions = inject(PermissionsService);

    moduleType = ModuleType.Tasks;
    tabID: string;
    tab: TabModel;
    data: Promise<TaskWorkspaceModel[]>;
    headerLookup: any = {};

    rendererConfig: RendererConfig[] = [
        {
            textColumn: "actionDate",
            actionColumn: "isOverdue",
            actionValue: "Yes",
            type: RendererType.DueDate,
            formatType: FormatType.DateFormat,
        },
        {
            textColumn: "scoreName",
            actionColumn: "scoreName_COLOR",
            hideActionColumn: true,
            type: RendererType.Score,
        },
    ];

    dateProps: string[] = ["actionDate"];

    permissions: WorkspaceModulePermissionModel;

    async ngOnInit() {
        this.permissions = this._permissions.permissions.Workspace.task;
        await this._translation.ready();
        this.data = this.getData();

        this._tabs.listeners.addGlobalListener(
            TabEventListenerType.OnSave,
            this.pages.TaskDetail,
            (taskID) => {
                this.tab.refresh();
            },
            this.tab
        );
    }

    getData() {
        let data = firstValueFrom(this._taskDs.getOverview());
        this.mapHeaders();

        return data;
    }

    async openDetail(task: TaskWorkspaceModel) {
        await this._translation.ready();
        const taskName = this._translation.t("overviewOpenDetailTabTitle", {
            identifier: task.taskIdentifier,
            name: task.taskName,
        });
        this._tabs.generateTab(this.pages.TaskDetail, task.guid, taskName);
    }

    private mapHeaders(): void {
        let headerLookup: any = {};
        const t = (key: string) => this._translation.t(key);

        headerLookup[nameof<TaskWorkspaceModel>("taskIdentifier")] = t("overviewIdentifierHeader");
        headerLookup[nameof<TaskWorkspaceModel>("taskName")] = t("overviewTaskNameHeader");
        headerLookup[nameof<TaskWorkspaceModel>("linkedItem")] = t("overviewLinkedItemHeader");
        headerLookup[nameof<TaskWorkspaceModel>("actionDate")] = t("overviewActionDateHeader");
        headerLookup[nameof<TaskWorkspaceModel>("status")] = t("overviewStatusHeader");
        headerLookup[nameof<TaskWorkspaceModel>("scoreName")] = t("overviewTaskScoreHeader");
        headerLookup[nameof<TaskWorkspaceModel>("scoreName_COLOR")] = t("overviewTaskScoreColorHeader");
        headerLookup[nameof<TaskWorkspaceModel>("responsibles")] = t("overviewResponsiblesHeader");
        headerLookup[nameof<TaskWorkspaceModel>("reviewers")] = t("overviewReviewersHeader");
        headerLookup[nameof<TaskWorkspaceModel>("comments")] = t("overviewCommentsHeader");
        headerLookup[nameof<TaskWorkspaceModel>("isOverdue")] = t("overviewIsOverdueHeader");

        this.headerLookup = headerLookup;
    }
}
